// /src/components/Footer.tsx

'use client';

import { useEffect, useState } from 'react';
import { useGallerySync } from '@/hooks/useGalleryAPI';
import { useGoogleReviewsSync } from '@/hooks/useGoogleReviewsSync';
import { GallerySync } from '@/components/GallerySync';
import { AwardsSync } from '@/components/AwardsSync'; // <-- use AwardsSync here
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import logoImage from '@/assets/fca-logo-updated.png';

export const Footer = () => {
  // Hooks
  const { syncGallery, isLoading: isGalleryLoading } = useGallerySync();
  const { syncReviews, isLoading: isReviewsLoading } = useGoogleReviewsSync();

  // Local "last synced" stamps shown next to the pills
  const [galleryLastSyncTime, setGalleryLastSyncTime] = useState<string>('');
  const [reviewsLastSyncTime, setReviewsLastSyncTime] = useState<string>('');

  // Load previous stamps on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setGalleryLastSyncTime(localStorage.getItem('gallery:lastSync') || '');
      setReviewsLastSyncTime(localStorage.getItem('reviews:lastSync') || '');
    }
  }, []);

  // Wrap handler so we can stamp the time on success
  const handleGallerySync = async () => {
    try {
      await syncGallery();
      const stamp = new Date().toLocaleString();
      setGalleryLastSyncTime(stamp);
      if (typeof window !== 'undefined') {
        localStorage.setItem('gallery:lastSync', stamp);
      }
    } catch {
      // hook surfaces its own errors; no-op here
    }
  };

  // Same idea for Google Reviews sync (used by the "Client Reviews Sync" button)
  const handleReviewsSync = async () => {
    try {
      await syncReviews();
      const stamp = new Date().toLocaleString();
      setReviewsLastSyncTime(stamp);
      if (typeof window !== 'undefined') {
        localStorage.setItem('reviews:lastSync', stamp);
      }
    } catch {
      // hook surfaces its own errors; no-op here
    }
  };


  return (
    <footer className="py-12 border-t border-border bg-background text-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-6">
          <div className="flex justify-center mb-4">
            <a href="/" className="inline-block">
              <div className="h-20 w-20 rounded-full bg-white/90 backdrop-blur-sm border border-border/20 flex items-center justify-center elegant-shadow p-2 hover:scale-105 transition-elegant">
                <img
                  src={logoImage}
                  alt="Frosted Cake Art Logo"
                  className="h-full w-full object-contain"
                />
              </div>
            </a>
          </div>

          <h3 className="font-heading text-primary">Frosted Cake Art</h3>

          <p className="max-w-2xl mx-auto">
            Creating sweet memories, one masterpiece at a time. Thank you for considering us for your special celebration.
          </p>

          {/* Copyright Line */}
          <div className="text-center text-sm text-muted-foreground mb-4">
            <span>© 2024 Frosted Cake Art. All rights reserved.</span>
          </div>

          {/* Privacy and Terms Line */}
          <div className="text-center text-sm text-muted-foreground mb-4">
            <div className="flex justify-center gap-4">
              <a href="/privacy-policy" className="hover:text-primary transition-elegant">Privacy Policy</a>
              <a href="/terms-of-service" className="hover:text-primary transition-elegant">Terms of Service</a>
            </div>
          </div>

          {/* Sync Buttons Line */}
          <div className="text-center">
            {/* make row wrap on small screens, reduce gap on small screens and keep larger gap on md+ */}
            <div className="flex flex-wrap justify-center items-center gap-3 md:gap-6 text-xs">
              {/* Wrap components in non-shrinking containers so they keep pill shape */}
              <div className="flex-shrink-0">
                <GallerySync
                  label="Gallery Sync"
                  onSync={handleGallerySync}
                  lastSyncTime={galleryLastSyncTime}
                  isLoading={isGalleryLoading}
                />
              </div>
          
              <div className="flex-shrink-0">
                <AwardsSync />
              </div>
          
              <div className="flex-shrink-0">
                <div className="flex items-center gap-4">
                  <Button
                    size="sm"
                    onClick={handleReviewsSync}
                    disabled={isReviewsLoading}
                    aria-label="Sync client reviews"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border bg-background text-foreground hover:bg-accent hover:text-white h-auto px-2 py-1 text-xs rounded-full border-muted-foreground/30 data-[state=loading]:opacity-60"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${isReviewsLoading ? "animate-spin" : ""}`} />
                    {isReviewsLoading ? "Syncing…" : "Client Reviews Sync"}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {reviewsLastSyncTime ? `Last synced: ${reviewsLastSyncTime}` : "Last synced: —"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Follow us on social media for daily inspiration and behind-the-scenes glimpses of our creative process.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
